import { ComputedRef, MaybeRefOrGetter, readonly, Ref, ref, toValue, watch } from "vue";
import { DSInputToken } from "@/src/design_system/form_elements/token_input/types";

interface UseTokenInputLiveRegionOptions {
  tokens: MaybeRefOrGetter<DSInputToken[]>;
  selectedIndex: ComputedRef<number | null>;
}

interface UseTokenInputLiveRegion {
  announcement: Readonly<Ref<string>>;
  announceRemoved: (token: DSInputToken) => void;
  expectAddedToken: () => void;
}

export const useTokenInputLiveRegion = ({
  tokens,
  selectedIndex,
}: UseTokenInputLiveRegionOptions): UseTokenInputLiveRegion => {
  const announcement = ref("");
  const awaitsCommittedToken = ref(false);
  const messages = window.I18n.designSystem.tokenInput;

  const announce = (template: string, token: DSInputToken): void => {
    announcement.value = template.replace("%{text}", () => token.label ?? token.text);
  };

  watch(selectedIndex, index => {
    const token = index === null ? undefined : toValue(tokens)[index];

    if (token) {
      announce(messages.announceSelected, token);
    }
  });

  watch(
    () => toValue(tokens),
    (next, previous) => {
      const awaited = awaitsCommittedToken.value;
      awaitsCommittedToken.value = false;

      if (!awaited || next.length <= previous.length) {
        return;
      }

      const added = next.find((token, index) => token.text !== previous[index]?.text);

      if (added) {
        announce(messages.announceAdded, added);
      }
    }
  );

  return {
    announcement: readonly(announcement),
    announceRemoved: token => announce(messages.announceRemoved, token),
    expectAddedToken: () => {
      awaitsCommittedToken.value = true;
    },
  };
};
